import type { PropsWithChildren } from 'react';
import { Toaster } from 'sonner';

// Sonner toaster, themed with our design tokens.
//
// We deliberately do NOT pass `richColors`: Sonner's preset palette is
// close to material design and doesn't match our brand. Instead each
// toast type is painted via `toastOptions.classNames` so success / error
// / warning / info pick up `--color-success` / `--color-destructive` /
// `--color-orangeDA` / `--color-primary` from globals.css. The Tailwind
// v4 important-suffix syntax (`bg-destructive!`) is required because
// Sonner sets `background-color: var(--normal-bg)` inline on every
// toast and a plain utility wouldn't otherwise win.
//
// Position is top-center (one common place for every toast in the app);
// closeButton renders the small × so users can dismiss without waiting for
// the duration timeout.
export function ToastProvider({ children }: PropsWithChildren) {
  return (
    <>
      {children}
      <Toaster
        position="top-center"
        closeButton
        toastOptions={{
          // 4000ms matches Sonner's default; explicit so any future
          // change is reviewed.
          duration: 4000,
          classNames: {
            // Shared shell. Border/shadow tokens keep toasts in the same
            // elevation family as dropdowns and cards.
            toast:
              'rounded-lg border shadow-elevation-2 text-sm font-medium tracking-tight gap-3 p-3 ps-4!',
            title: 'font-semibold leading-tight',
            description: 'opacity-90 leading-snug',
            closeButton: 'bg-transparent text-current hover:opacity-70',
            // Per-type palettes. Suffix `!` is Tailwind v4 important.
            error: 'bg-destructive! text-destructive-foreground! border-destructive!',
            success: 'bg-success! text-white! border-success!',
            warning: 'bg-orangeDA! text-white! border-orangeDA!',
            info: 'bg-primary! text-white! border-primary!',
          },
        }}
      />
    </>
  );
}
